import { useCallback, useEffect, useState } from 'preact/hooks';
import type { Asset, Category } from '@schibsted-svp/svp-api-types';

import { useApis } from './ApisProvider';
import { useAbortController } from './useAbortController';

const FETCH_LIMIT = 5;

export const useSeasonAssets = ({
  categoryId,
  seasonNumber,
}: {
  categoryId: Category['id'];
  seasonNumber: number;
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [nextPage, setNextPage] = useState<number>();
  const { getSignal } = useAbortController();
  const { svpApi } = useApis();

  useEffect(() => {
    setAssets([]);
    setNextPage(undefined);

    svpApi.seasons
      .getEpisodes(
        {
          categoryId,
          seasonNumber,
          sort: '-episodeNumber',
          limit: FETCH_LIMIT,
        },
        { signal: getSignal() }
      )
      .then((data) => {
        setAssets(data.assets);
        setNextPage(data.nextPage);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error(err);
      });
  }, [svpApi, getSignal, categoryId, seasonNumber]);

  const fetchNextPage = useCallback(async () => {
    const hasNextPage = Boolean(nextPage);

    if (!hasNextPage) {
      return;
    }

    svpApi.seasons
      .getEpisodes(
        {
          categoryId,
          seasonNumber,
          sort: '-episodeNumber',
          limit: FETCH_LIMIT,
          page: nextPage,
        },
        { signal: getSignal() }
      )
      .then((data) => {
        setAssets((prevAssets) => [...prevAssets, ...data.assets]);
        setNextPage(data.nextPage);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error(err);
      });
  }, [nextPage, svpApi, getSignal, categoryId, seasonNumber]);

  return { assets, fetchNextPage };
};
